import React, { useState } from 'react';
import { 
  Smartphone, 
  Monitor, 
  Apple, 
  Laptop, 
  Globe, 
  Cpu, 
  Activity, 
  Radio, 
  Edit3, 
  Check, 
  X,
  Wifi,
  QrCode,
  ShieldCheck,
  Zap
} from 'lucide-react';
import { DeviceInfo, DeviceOS } from '../types';

interface DevicePresenceProps {
  currentDevice: DeviceInfo;
  peers: DeviceInfo[];
  onRenameDevice?: (name: string) => void;
  onOpenQr?: () => void;
}

export const DevicePresence: React.FC<DevicePresenceProps> = ({ 
  currentDevice, 
  peers, 
  onRenameDevice,
  onOpenQr 
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(currentDevice.deviceName);

  const handleSaveName = () => {
    if (editedName.trim() && onRenameDevice) {
      onRenameDevice(editedName.trim());
    }
    setIsEditingName(false);
  };

  const getOsIcon = (os: DeviceOS) => {
    switch (os) {
      case 'android':
        return <Smartphone size={16} className="text-emerald-400" />;
      case 'ios':
      case 'mac':
        return <Apple size={16} className="text-slate-200" />;
      case 'windows':
        return <Monitor size={16} className="text-cyan-400" />;
      case 'linux':
        return <Cpu size={16} className="text-amber-400" />;
      case 'extension':
        return <Laptop size={16} className="text-purple-400" />;
      default:
        return <Globe size={16} className="text-cyan-400" />;
    }
  };

  const getLatencyBadge = (latency?: number, isMe?: boolean) => {
    const val = latency || (isMe ? 2 : 18);
    let colorClass = 'text-emerald-400 bg-emerald-950/40 border-emerald-800/40';
    if (val > 100) {
      colorClass = 'text-amber-400 bg-amber-950/40 border-amber-800/40';
    }
    if (val > 300) {
      colorClass = 'text-rose-400 bg-rose-950/40 border-rose-800/40';
    }

    return (
      <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-md border ${colorClass} flex items-center gap-1`}>
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping"></span>
        <span>{val}ms</span>
      </span>
    );
  };

  const allDevices = [currentDevice, ...peers];

  return (
    <div className="native-glass rounded-2xl p-4 border border-white/10 shadow-xl relative overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between pb-3 mb-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-cyan-950/60 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
            <Radio size={14} className="animate-pulse" />
          </div>
          <div>
            <h3 className="text-xs font-bold tracking-wider text-slate-200 uppercase font-display">
              Mesh Radar ({allDevices.length})
            </h3>
            <p className="text-[10px] text-slate-400 font-mono">Zero-Knowledge Peer Mesh</p>
          </div>
        </div>

        {onOpenQr && (
          <button
            onClick={onOpenQr}
            className="text-[11px] font-semibold text-cyan-400 hover:text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 px-2 py-1 rounded-lg border border-cyan-500/30 flex items-center gap-1 transition-all"
            title="Scan QR code to pair another device"
          >
            <QrCode size={12} />
            <span>Pair</span>
          </button>
        )}
      </div>

      {/* Devices List */}
      <div className="space-y-2">
        {allDevices.map((d, index) => {
          const isMe = d.deviceId === currentDevice.deviceId;
          return (
            <div
              key={d.deviceId + index}
              className={`flex items-center justify-between p-2.5 rounded-xl border transition-all ${
                isMe
                  ? 'bg-gradient-to-r from-cyan-950/40 to-purple-950/30 border-cyan-500/40 shadow-inner'
                  : 'bg-[#080A14] border-white/5 hover:border-white/15'
              }`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-black/60 flex items-center justify-center flex-shrink-0 border border-white/10 shadow-inner">
                  {getOsIcon(d.os)}
                </div>

                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    {isMe && isEditingName ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={editedName}
                          onChange={(e) => setEditedName(e.target.value)}
                          className="glass-input text-xs py-0.5 px-1.5 w-24"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveName();
                            if (e.key === 'Escape') setIsEditingName(false);
                          }}
                        />
                        <button onClick={handleSaveName} className="text-emerald-400 p-0.5">
                          <Check size={11} />
                        </button>
                        <button onClick={() => setIsEditingName(false)} className="text-rose-400 p-0.5">
                          <X size={11} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs font-bold text-white truncate max-w-[110px]">{d.deviceName}</p>
                        {isMe && (
                          <button
                            onClick={() => setIsEditingName(true)}
                            className="text-slate-500 hover:text-cyan-300 transition-colors p-0.5"
                            title="Rename your device"
                          >
                            <Edit3 size={10} />
                          </button>
                        )}
                      </>
                    )}

                    {isMe && !isEditingName && (
                      <span className="text-[8px] uppercase tracking-wider font-extrabold bg-cyan-400 text-black px-1.5 py-0.2 rounded font-mono">
                        YOU
                      </span>
                    )}
                  </div>

                  <p className="text-[10px] text-slate-400 capitalize flex items-center gap-1 mt-0.5 font-mono">
                    <span>{d.os}</span>
                    <span>•</span>
                    <span className="text-slate-500">{d.clientType}</span>
                  </p>
                </div>
              </div>

              <div className="flex items-center flex-shrink-0">
                {getLatencyBadge(d.latencyMs, isMe)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
